import React, { useEffect } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { Check, ArrowRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import PageHero from '../components/PageHero';
import GuaranteeCard from '../components/GuaranteeCard';
import Reviews from '../components/Reviews';
import FAQ from '../components/FAQ';
import { ContactSection, Footer } from '../components/Footer';
import { getStartupService, startupServices } from '../data/startupServices';

const setMeta = (svc) => {
  document.title = `${svc.title} | EasyWorks — Startup & Compliance`;
  const desc = `${svc.short} Starting ${svc.price}. Book a free consultation with EasyWorks.`;
  let m = document.querySelector('meta[name="description"]');
  if (!m) { m = document.createElement('meta'); m.setAttribute('name', 'description'); document.head.appendChild(m); }
  m.setAttribute('content', desc);
};

export default function StartupServicePage() {
  const { subslug } = useParams();
  const svc = getStartupService(subslug);
  useEffect(() => { if (svc) setMeta(svc); }, [svc]);
  useEffect(() => { window.scrollTo(0, 0); }, [subslug]);
  if (!svc) return <Navigate to="/startup-and-compliance" replace />;
  return (
    <div className="min-h-screen bg-[#FAF2E8] font-[Inter]" data-testid={`startup-service-page-${subslug}`}>
      <Navbar />
      <PageHero
        eyebrow={`Startup & Compliance · ${svc.title}`}
        headline={svc.hero.headline}
        headlineAccent={svc.hero.accent}
        description={svc.hero.description}
        category="startup"
        subtype={svc.title}
        service={svc.title}
        formTitle={`Get ${svc.title} quote`}
        formSubtitle="Custom pricing and timeline within 24 hours."
        formTestId={`startup-quote-form-${subslug}`}
        ctaLabel="Book Consultation"
      />

      <section className="bg-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-2 gap-10">
          <div>
            <div className="text-xs uppercase tracking-wider text-[#F39200] font-semibold mb-3">What&apos;s included</div>
            <h2 className="text-2xl md:text-3xl font-extrabold text-neutral-900 mb-6">Everything covered end-to-end</h2>
            <ul className="space-y-3">
              {svc.inclusions.map((f) => (
                <li key={f} className="flex items-start gap-3 text-[15px] text-neutral-800">
                  <span className="mt-1 w-5 h-5 rounded-full bg-[#F39200]/10 text-[#F39200] flex items-center justify-center shrink-0">
                    <Check size={13} />
                  </span>
                  {f}
                </li>
              ))}
            </ul>
            <div className="mt-8 inline-block bg-[#FAF2E8]/60 border border-black/5 rounded-xl px-4 py-3">
              <div className="text-xs text-neutral-500 uppercase tracking-wide font-semibold">Starting price</div>
              <div className="mt-1 text-2xl font-extrabold text-neutral-900">{svc.price}</div>
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-[#F39200] font-semibold mb-3">Documents you need</div>
            <h2 className="text-2xl md:text-3xl font-extrabold text-neutral-900 mb-6">Keep these ready</h2>
            <ul className="space-y-3">
              {svc.documents.map((d) => (
                <li key={d} className="flex items-start gap-3 text-[15px] text-neutral-800">
                  <span className="mt-1 w-5 h-5 rounded-full bg-neutral-900/5 text-neutral-700 flex items-center justify-center shrink-0 font-bold text-[11px]">i</span>
                  {d}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-[#FAF2E8] py-14">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <h3 className="text-xl md:text-2xl font-extrabold text-neutral-900">Other services in Startup & Compliance</h3>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {startupServices.filter((x) => x.slug !== svc.slug).map((x) => (
              <Link key={x.slug} to={`/startup-and-compliance/${x.slug}`} data-testid={`startup-related-${x.slug}`} className="bg-white border border-black/5 rounded-xl p-5 hover:border-[#F39200] hover:shadow-md transition-all">
                <div className="text-sm font-bold text-neutral-900">{x.title}</div>
                <div className="mt-1 text-xs text-neutral-500">{x.tagline}</div>
                <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#F39200]">Learn more <ArrowRight size={12} /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <GuaranteeCard />
      <Reviews />
      <FAQ />
      <ContactSection />
      <Footer />
    </div>
  );
}
